import { MissionBadge } from '@/features/missions/components/MissionBadge'
import { useNectarTask } from '@/features/nectar/api'
import type { AudioAnnotationField, AudioAnnotationTaskDetail } from '@/features/nectar/api/models'
import { NectarAudioPlayer } from '@/features/nectar/components/shared/NectarAudioPlayer'
import { useSubmitAudioAnnotation } from '@/features/nectar/hooks/useSubmitAudioAnnotation'
import { useTurnstileCaptcha } from '@/features/turnstile'
import { Button, Spinner, Text } from '@/ui-kit'
import { Trans, useLingui } from '@lingui/react/macro'
import { clsx } from 'clsx'
import { Check, Lock } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import type { TaskRendererProps } from '../task-types'
import { UnsupportedTaskFallback } from '../UnsupportedTaskFallback'
import { FIELD_COMPONENTS } from './fields'
import { getDefaultAnswer, validateAnswers } from './validateAnswers'

const buildInitialAnswers = (fields: AudioAnnotationField[]): Record<string, unknown> => {
  const out: Record<string, unknown> = {}
  for (const field of fields) out[field.key] = getDefaultAnswer(field)
  return out
}

export function AudioAnnotationTask({ taskId, taskIndex, projectId, captchaEnabled, onSubmitted }: TaskRendererProps) {
  const { t } = useLingui()
  const { data: task, isFetching: isLoadingTask } = useNectarTask(projectId, taskId)
  const taskDetail = task as unknown as AudioAnnotationTaskDetail
  const fields = useMemo(() => taskDetail?.formSchema?.fields ?? [], [taskDetail?.formSchema?.fields])

  const captcha = useTurnstileCaptcha({ enabled: captchaEnabled, action: 'submission' })
  const { submit, isSubmitting } = useSubmitAudioAnnotation({ captcha })

  const [answers, setAnswers] = useState<Record<string, unknown>>(() => buildInitialAnswers(fields))
  const [hasListened, setHasListened] = useState(false)
  const [audioError, setAudioError] = useState(false)

  const audioUrl = taskDetail?.audioUrl

  useEffect(() => {
    setHasListened(false)
    setAudioError(false)
  }, [audioUrl])

  const isValid = useMemo(() => validateAnswers(fields, answers), [fields, answers])

  const setFieldValue = (key: string, value: unknown) => {
    setAnswers((prev) => ({ ...prev, [key]: value }))
  }

  const onSubmit = async () => {
    if (!isValid || !hasListened || isSubmitting) return
    await submit({
      projectId,
      taskId: taskId,
      taskIndex,
      answers,
      onSuccess: () => onSubmitted(taskId)
    })
  }

  if (!isLoadingTask && fields.length === 0) {
    return <UnsupportedTaskFallback taskType={taskDetail.type} />
  }

  const submitDisabled = !isValid || !hasListened || isSubmitting || (captcha.active && !captcha.token)

  const submitHint =
    !audioUrl || audioError ? t`Audio could not be loaded` : !hasListened ? t`Listen to the audio first` : undefined

  return (
    <div style={{ animation: 'nectar-slide-up 0.4s ease-out' }}>
      <div className="flex flex-col items-stretch gap-6 rounded-2xl border border-solid border-gray-100 px-8 py-10">
        {taskDetail?.instruction && (
          <Text
            type="body2-regular"
            className="text-gray-90 m-auto block rounded-xl border border-solid border-gray-100 p-2"
          >
            {taskDetail?.instruction}
          </Text>
        )}
        {taskDetail?.phrase && (
          <span className="text-h5 block text-center leading-[120%]">&ldquo;{taskDetail?.phrase}&rdquo;</span>
        )}
        {taskDetail?.topic && (
          <div className="flex justify-center">
            <MissionBadge variant="warning">{taskDetail?.topic}</MissionBadge>
          </div>
        )}

        <div className="-mx-8 border-b border-solid border-gray-100 px-8 py-4">
          {audioUrl ? (
            <NectarAudioPlayer
              audioUrl={audioUrl}
              onFinish={() => setHasListened(true)}
              onError={() => setAudioError(true)}
            />
          ) : (
            <div className="flex h-10 w-full items-center justify-center">
              {isLoadingTask ? (
                <Spinner size="small" />
              ) : (
                <Text type="body3-regular" className="text-gray-90">
                  <Trans>Audio unavailable</Trans>
                </Text>
              )}
            </div>
          )}
        </div>

        <div
          className="flex items-center gap-3 rounded-xl border border-solid border-gray-100 bg-gray-100/30 px-4 py-3"
          role="status"
          aria-live="polite"
        >
          {hasListened ? (
            <>
              <Check className="text-green h-4 w-4 shrink-0" />
              <Text type="body3-regular" className="text-gray-70">
                <Trans>Audio played — you can answer now.</Trans>
              </Text>
            </>
          ) : (
            <>
              <Lock className="text-gray-70 h-4 w-4 shrink-0" />
              <Text type="body3-regular" className="text-gray-70">
                {!audioUrl || audioError ? (
                  <Trans>Audio could not be loaded — you can’t annotate this task.</Trans>
                ) : (
                  <Trans>Listen to the audio to the end before answering.</Trans>
                )}
              </Text>
            </>
          )}
        </div>

        <fieldset
          disabled={!hasListened}
          aria-disabled={!hasListened}
          className={clsx(
            'm-0 flex min-w-0 flex-col gap-6 border-0 p-0 transition-opacity',
            !hasListened && 'pointer-events-none opacity-50'
          )}
        >
          {fields.map((field) => {
            const Component = FIELD_COMPONENTS.get(field.type)
            if (!Component) return null
            return (
              <Component
                key={field.key}
                field={field}
                value={answers[field.key]}
                onChange={(value) => setFieldValue(field.key, value)}
              />
            )
          })}
        </fieldset>

        {captcha.renderWidget('w-full')}

        <Button
          variant="green"
          onClick={onSubmit}
          isDisabled={submitDisabled}
          isLoading={isSubmitting}
          className="w-full"
          title={submitHint}
        >
          {isSubmitting ? <Trans>Sending...</Trans> : <Trans>Submit</Trans>}
        </Button>
      </div>
    </div>
  )
}
